import { Grid } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import React from 'react';
import { HdIcon, HdTooltip } from '../../../../components';
import { DESTINATION_TYPES } from '../../constants/destinationTypes';
import { TRACKER_DESTINATION_SQL_HOST_NAME_BLUR } from '../../../../connector/connector-form/tracking/connectorFormTracking';
import { HdFormikPasswordField, HdFormikTextField } from '../../../../form/formik/components';
import { FormikAutocomplete } from '@hevoio/hevo-ui-toolkit';
import SSHConfig from '../../../../connector/connector-form/components/ssh-config/SSHConfig';
import SSHConfigFields from '../../../../connector/connector-form/components/ssh-config/SSHConfigFields';
import { SSHConfigHelpBanner } from '../../../../connector/connector-form/components/ssh-config-help-banner/SSHConfigHelpBanner';
import SSLConfig from '../../../../connector/connector-form/components/ssl-config/SSLConfig';
import { SanitizeName } from '../../../../connector/connector-form/components/connector-reusable-options/ConnectorReusableOptions';
import HdFormControl from '../../../../form/components/form-control/HdFormControl';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import useLocalHostTracker from '../../../../connector/connector-form/hooks/useLocalHostTracker';
import { ConfigDestinationTypeBaseProps } from '../interface';
// eslint-disable-next-line import/no-cycle
import { getHostName } from './experimentUtils';
import { MS_SQL_AUTH_OPTIONS, MS_SQL_AUTH_METHOD } from './constants';

export default function Sql({
  hdLocalHostValidatorDisabled,
  isEditing,
  maskSecretFields,
  destinationTypeIdentifier,
  destinationTypeMetaData,
  hevoEntityFor,
  onInviteMember,
  rawDestinationType,
  isDestinationModeWarehouse
}: ConfigDestinationTypeBaseProps) {
  const destinationTypeTrackingProps = {
    destinationType: destinationTypeIdentifier,
    context: hevoEntityFor
  };

  const analyticsTracker = useAnalyticsTracker();

  const formikProps = useFormikContext<{
    host: string;
    isConnectionUri: string;
    connectionString: string;
    port: string;
    databaseName: string;
    useSSH: boolean;
    authenticationType: MS_SQL_AUTH_METHOD | null;
  }>();

  useLocalHostTracker({
    value: formikProps.values.host,
    hdLocalHostValidatorDisabled,
    localHostTrackerLabel: 'db-host-field-local-host-steps',
    localHostTrackerSection: 'destination_settings',
    localHostTrackingParams: {
      destinationType: destinationTypeIdentifier,
      context: hevoEntityFor.toString()
    }
  });

  const isDestinationTypeRedshift = () =>
    DESTINATION_TYPES.REDSHIFT.name === destinationTypeIdentifier;

  const isMsSql = () => destinationTypeIdentifier === 'MS_SQL';

  const onHostNameBlur = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_DESTINATION_SQL_HOST_NAME_BLUR,
      properties: {
        hostName: formikProps.values.host,
        destinationType: destinationTypeIdentifier
      }
    });
  };

  const canProvideSchemaName = () => destinationTypeMetaData.provideSchemaName;

  const canUseSSL = () => destinationTypeMetaData.canUseSSL;

  return (
    <>
      {isMsSql() ? (
        <Grid container spacing={2} className='mb-5'>
          <Grid item xs={12} md={6}>
            <Field
              name='authenticationType'
              component={FormikAutocomplete}
              dataId='authentication-type-field'
              options={MS_SQL_AUTH_OPTIONS}
              disableClearable
              getOptionLabel={(option: typeof MS_SQL_AUTH_OPTIONS[0]) => option?.label ?? ''}
              isOptionEqualToValue={(
                option: typeof MS_SQL_AUTH_OPTIONS[0],
                value: typeof MS_SQL_AUTH_OPTIONS[0]
              ) => option.value === value.value}
              renderOption={(
                props: React.HTMLAttributes<HTMLLIElement> & { key: string },
                option: typeof MS_SQL_AUTH_OPTIONS[0]
              ) => {
                const { key, ...optionProps } = props;
                return <li key={key} {...optionProps}>{option.label}</li>;
              }}
              textFieldProps={{ label: 'Authentication type' }}
            />
          </Grid>
        </Grid>
      ) : null}

      <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <HdFormControl>
              <Field
                name='host'
                label={isDestinationTypeRedshift() ? 'Database Hostname' : 'Database Host'}
                required
                placeholder={
                  isDestinationTypeRedshift() ? 'IP (1.2.3.4) or host(your.server.com)' : 'DB Host'
                }
                component={HdFormikTextField}
                onBlur={onHostNameBlur}
                helperText={getHostName(destinationTypeIdentifier, destinationTypeTrackingProps)}
              />
            </HdFormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <HdFormControl>
              <Field
                name='port'
                label='Database Port'
                required
                component={HdFormikTextField}
                type='number'
                helperText="The port on which the database is accepting connections."
                endAdornment={
                  <HdTooltip
                    title='Default value: 1433. Update if your instance uses a different port'
                    placement='top'
                  >
                    <HdIcon name='info' />
                  </HdTooltip>
                }
              />
            </HdFormControl>
          </Grid>
        </Grid>

      {!isMsSql() || formikProps.values.authenticationType === MS_SQL_AUTH_METHOD.DATABASE_HOST ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <HdFormControl>
              <Field name='user' label='Database User' required component={HdFormikTextField} />
            </HdFormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <HdFormControl>
              <Field
                name='password'
                label='Database Password'
                required
                masked={maskSecretFields}
                component={HdFormikPasswordField}
              />
            </HdFormControl>
          </Grid>
        </Grid>
      ) : null}

      {isMsSql() && formikProps.values.authenticationType === MS_SQL_AUTH_METHOD.ENTRA_ID ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <HdFormControl>
                <Field
                  name='databaseName'
                  label='Database Name'
                  required
                  component={HdFormikTextField}
                  helperText='The name of an existing database you want to load data into'
                />
              </HdFormControl>
            </Grid>

           
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <HdFormControl>
                <Field
                  name='clientId'
                  label='Client ID'
                  required
                  component={HdFormikTextField}
                  helperText='The unique ID of your app registered in Microsoft Entra ID'
                />
              </HdFormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <HdFormControl>
                <Field
                  name='clientSecret'
                  label='Client Secret'
                  required
                  masked={maskSecretFields}
                  component={HdFormikPasswordField}
                  helperText='The secret generated for your registered app'
                />
              </HdFormControl>
            </Grid>
          </Grid>
        </>
      ) : null}

      {!isMsSql() || formikProps.values.authenticationType !== MS_SQL_AUTH_METHOD.ENTRA_ID ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <HdFormControl>
              <Field
                name='databaseName'
                label='Database Name'
                required
                component={HdFormikTextField}
                helperText='The name of an existing database you want to load data into'
              />
            </HdFormControl>
          </Grid>

          {canProvideSchemaName() ? (
            <Grid item xs={12} md={6}>
              <HdFormControl>
                <Field name='schemaName' label='Schema Name' component={HdFormikTextField}
                helperText="The name of the schema you want to load data into" />
              </HdFormControl>
            </Grid>
          ) : null}
        </Grid>
      ) : null}

      <SSHConfig className='mb-7' entityTypeIdentifier='database'>
        {formikProps.values.useSSH ? (
          <SSHConfigHelpBanner
            destinationTypeDisplayName={rawDestinationType.dest_type_display}
            defaultExpanded
            onInviteMember={onInviteMember}
          />
        ) : null}

        <SSHConfigFields />
      </SSHConfig>

      {canUseSSL() ? (
        <SSLConfig sslCARequired={!!destinationTypeMetaData.sslCARequired} />
      ) : null}

      {!isDestinationModeWarehouse ? (
        <div className='mb-5'>
          <SanitizeName
            disabled={!!isEditing}
          />
        </div>
      ) : null}
    </>
  );
}
